import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface ProjectRequest {
  id: string;
  project_id: string;
  message: string;
  status: 'pending' | 'accepted' | 'rejected' | 'withdrawn';
  proposed_delivery_date?: string;
  proposed_price?: number;
  created_at: string;
  updated_at: string;
  project?: {
    id: string;
    title: string;
    institution: string;
    budget: number;
    deadline: string;
    status: string;
    client_name: string;
  };
}

const FreelancerRequests: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ProjectRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRequests: 0,
    hasNext: false,
    hasPrev: false
  });

  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          navigate('/signin');
          return;
        }

        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        if (parsedUser.role !== 'freelancer') {
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/signin');
      }
    };

    loadUserData();
  }, [navigate]);

  const loadRequests = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value.toString());
      });

      const response = await axios.get(
        `http://localhost:5000/api/projects/freelancer/requests?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setRequests(response.data.data.requests);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (user?.role === 'freelancer') {
      loadRequests();
    }
  }, [user, loadRequests]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleWithdrawRequest = async (requestId: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to withdraw this request?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `http://localhost:5000/api/projects/request/${requestId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        alert('Request withdrawn successfully');
        loadRequests();
      }
    } catch (error: any) {
      console.error('Error withdrawing request:', error);
      alert(error.response?.data?.message || 'Error withdrawing request');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'withdrawn': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusCounts = () => {
    return requests.reduce((counts, request) => {
      counts[request.status] = (counts[request.status] || 0) + 1;
      counts.total++;
      return counts;
    }, { total: 0 } as Record<string, number>);
  };

  const statusCounts = getStatusCounts();

  if (isLoading && requests.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your requests...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          
          <div className="navbar-actions">
            <div className="user-info">
              <div className="user-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'F'}
              </div>
              <div className="user-details">
                <span className="user-name">{user?.username?.toUpperCase()}</span>
                <div className="user-meta">
                  <span className="role-badge role-freelancer">Freelancer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">My Project Requests</h1>
              <p className="text-gray-600">
                Track the status of your project requests
              </p>
            </div>
            <div className="flex space-x-3">
              <button 
                onClick={() => navigate('/projects')}
                className="btn btn-primary"
              >
                Browse Projects
              </button>
              <button 
                onClick={() => navigate('/dashboard')}
                className="btn btn-secondary"
              >
                ← Dashboard
              </button>
            </div>
          </div>

          {/* Status Filters */}
          <div className="flex space-x-2 mb-4 overflow-x-auto pb-2">
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'all', page: 1 }))}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                filters.status === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              All ({statusCounts.total})
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'pending', page: 1 }))}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                filters.status === 'pending'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              Pending ({statusCounts.pending || 0})
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'accepted', page: 1 }))}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                filters.status === 'accepted'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              Accepted ({statusCounts.accepted || 0})
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'rejected', page: 1 }))}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                filters.status === 'rejected'
                  ? 'bg-red-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              Rejected ({statusCounts.rejected || 0})
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'withdrawn', page: 1 }))}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                filters.status === 'withdrawn'
                  ? 'bg-gray-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              Withdrawn ({statusCounts.withdrawn || 0})
            </button>
          </div>
        </div>

        {/* Requests List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Requests ({pagination.totalRequests})
              </h2>
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                  placeholder="Search requests..."
                  className="form-input w-64"
                />
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="form-input"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="withdrawn">Withdrawn</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table Body */}
          {requests.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No requests found</h3>
              <p className="text-gray-600 mb-6">
                {filters.status !== 'all' || filters.search
                  ? 'Try adjusting your filters to find your requests.'
                  : "You haven't made any project requests yet."}
              </p>
              {(!filters.search && filters.status === 'all') && (
                <button
                  onClick={() => navigate('/projects')}
                  className="btn btn-primary"
                >
                  Browse Available Projects
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Project Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Your Message
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Request Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {requests.map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">
                              {request.project?.title || 'Project details not available'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.project?.institution} • Budget: ₦{request.project?.budget.toLocaleString()}
                            </div>
                            <div className="text-sm text-gray-500">
                              Client: {request.project?.client_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 line-clamp-3">
                            {request.message}
                          </div>
                          {request.proposed_price && (
                            <div className="text-xs text-gray-500 mt-1">
                              Proposed: ₦{request.proposed_price.toLocaleString()}
                            </div>
                          )}
                          {request.proposed_delivery_date && (
                            <div className="text-xs text-gray-500">
                              Delivery: {formatDate(request.proposed_delivery_date)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                            {request.status}
                          </span>
                          {request.status === 'accepted' && (
                            <div className="text-xs text-green-600 mt-1">
                              Project assigned!
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{formatDate(request.created_at)}</div>
                          <div className="text-xs text-gray-500">
                            Updated {formatDate(request.updated_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            {request.project && (
                              <button
                                onClick={() => navigate(`/projects/${request.project_id}`)}
                                className="btn btn-secondary btn-sm"
                              >
                                View Project
                              </button>
                            )}
                            {request.status === 'accepted' && request.project && (
                              <button
                                onClick={() => navigate(`/freelancer/projects/${request.project_id}/upload`)}
                                className="btn btn-primary btn-sm"
                              >
                                Start Work
                              </button>
                            )}
                            {request.status === 'pending' && (
                              <button
                                onClick={() => handleWithdrawRequest(request.id)}
                                className="btn btn-danger btn-sm"
                              >
                                Withdraw
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(filters.page - 1) * filters.limit + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(filters.page * filters.limit, pagination.totalRequests)}
                      </span> of{' '}
                      <span className="font-medium">{pagination.totalRequests}</span> requests
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={!pagination.hasPrev}
                        className={`px-3 py-2 rounded-md ${
                          pagination.hasPrev
                            ? 'bg-white text-gray-700 hover:bg-gray-50 border'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        ← Previous
                      </button>
                      
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 rounded-md ${
                              pagination.currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasNext}
                        className={`px-3 py-2 rounded-md ${
                          pagination.hasNext
                            ? 'bg-white text-gray-700 hover:bg-gray-50 border'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreelancerRequests;